import { Button, CircularProgressBar, Dropdown, Paragraph, TextField, Title } from '@cambridgeassessment/cambridge-ui';
import {
  BUSINESS_UNITS,
  EntryComponent,
  QualificationMin,
  Series,
  ALPHA_NUMERIC_ONLY_REGEX,
  buildNow,
} from '@cambridgeassessment/lib-ee-commons';
import * as seriesThunks from '../../../../store/series/thunks';
import * as sectionsThunks from '../../../../store/sections/thunks';
import actions from '../../../../store/sections/actions';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { StyledInputContainer } from '../../Content.styles';
import { Assessment } from '../../../../types/Syllabuses';
import { getCentreWithModerators } from '../../../../store/postResults/thunks';
import CentreCardContent from './CentreCardContent';
import { ButtonContainer } from './PostResultsPage.styles';
import Loader from '../../../../components/Loader/Loader';
import { ProgressContainer } from '../../WorkDownloading/components/WorkDownloading.styles';

const MAX_CENTRE_ID_LENGTH = 5;

const renderSeries = (item: Series): string => item.name;
const renderQualifications = (item: QualificationMin): string => item.name;
const renderAssessments = (item: Assessment): string => item.code;
const renderComponents = (item: EntryComponent): string => item.number;

interface PostResultsFields {
  assessment?: boolean;
  component?: boolean;
  qualification?: boolean;
}

interface QueryParams {
  seriesId?: string;
  qualificationId?: string;
  assessmentCode?: string;
  componentNumber?: string;
  centreId?: string;
}

interface InputsState {
  series: Series | null;
  qualification: QualificationMin | null;
  assessment: Assessment | null;
  component: EntryComponent | null;
  centreId: string;
}

const initialInputsState = {
  series: null,
  qualification: null,
  assessment: null,
  component: null,
  centreId: '',
};

const Selector = (): React.ReactElement => {
  const dispatch = useDispatch();
  const {
    userData: { businessUnit },
    series: { isLoading: isSeriesLoading, seriesData, hasError },
    sections: { qualifications, assessments, components },
    postResults: { centre, consortium, moderators, allocatedModerator, isFetchingCentre },
  } = useSelector((state: RootState) => state);
  const [inputs, setInputs] = useState<InputsState>({ ...initialInputsState });
  const [queryParams, setQueryParams] = useState<QueryParams>();
  const [errors, setErrors] = useState<{ centreId?: string }>({});
  const [showSearchResults, setShowSearchResults] = useState(false);

  const now = buildNow()();
  const activeSeries = seriesData?.filter((it) => now.isBetween(it.startDate, it.endDate)) || [];
  const titleByBusinessId = businessUnit?.id === BUSINESS_UNITS.CIE.id ? 'syllabus' : 'specification';

  useEffect(() => {
    if (!businessUnit) return;

    dispatch(seriesThunks.getSeriesData(businessUnit.id));
  }, [businessUnit, dispatch]);

  const resetFields = ({ qualification, assessment, component }: PostResultsFields): void => {
    const clearParams: QueryParams = {};

    if (qualification) {
      setInputs((prevState) => ({ ...prevState, qualification: null }));
      clearParams.qualificationId = undefined;
    }
    if (assessment) {
      setInputs((prevState) => ({ ...prevState, assessment: null }));
      clearParams.assessmentCode = undefined;
    }
    if (component) {
      setInputs((prevState) => ({ ...prevState, component: null }));
      clearParams.componentNumber = undefined;
    }

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, ...clearParams }));
    dispatch(actions.setFileResults(undefined));
  };

  const onSeriesSelected = (series: Series): void => {
    if (inputs.series?.id === series.id) return;

    setInputs((prevState) => ({ ...prevState, series }));

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, seriesId: series.id.toString() }));

    if (!businessUnit) return;

    dispatch(seriesThunks.getDetailedSeries(businessUnit.id, series.id));
    dispatch(sectionsThunks.getQualifications(businessUnit.id, series.id));
    setShowSearchResults(false);
    resetFields({ assessment: true, component: true, qualification: true });
  };

  const onQualificationSelected = (qualification: QualificationMin): void => {
    if (inputs.qualification?.id === qualification.id || !businessUnit || !inputs.series) return;

    setInputs((prevState) => ({ ...prevState, qualification }));
    dispatch(sectionsThunks.getAssessments(businessUnit.id, inputs.series.id, qualification.id));

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, qualificationId: qualification.id.toString() }));
    setShowSearchResults(false);
    resetFields({ assessment: true, component: true });
  };

  const onAssessmentSelected = (assessment: Assessment): void => {
    if (inputs.assessment?.code === assessment.code || !businessUnit || !inputs.series || !inputs.qualification) return;

    setInputs((prevState) => ({ ...prevState, assessment }));
    dispatch(sectionsThunks.getComponents(businessUnit.id, inputs.series.id, inputs.qualification.id, assessment.code));

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, assessmentCode: assessment.code }));
    setShowSearchResults(false);
    resetFields({ component: true });
  };

  const onComponentSelected = (component: EntryComponent): void => {
    if (
      inputs.component?.number === component.number ||
      !businessUnit ||
      !inputs.series ||
      !inputs.qualification ||
      !inputs.assessment
    )
      return;

    setInputs((prevState) => ({ ...prevState, component }));
    setShowSearchResults(false);
    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, componentNumber: component.number }));
  };

  const onCentreChange = (value: string): void => {
    const alphanumericOnly = ALPHA_NUMERIC_ONLY_REGEX;
    const matched = alphanumericOnly.exec(value);

    setErrors((prevErrors) => ({ ...prevErrors, centreId: undefined }));

    if (!matched) {
      setErrors((prevErrors) => ({ ...prevErrors, centreId: 'Centre number must contain letters and numbers only' }));
    } else if (value.length > MAX_CENTRE_ID_LENGTH) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        centreId: `Centre number cannot exceed ${MAX_CENTRE_ID_LENGTH} characters`,
      }));
    }
    setShowSearchResults(false);
    setInputs((prevState) => ({ ...prevState, centreId: value }));
    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, centreId: value }));
  };

  const isValidForm = useCallback(
    (): boolean =>
      !!inputs.series &&
      !!inputs.qualification &&
      !!inputs.assessment &&
      !!inputs.component &&
      !!inputs.centreId &&
      !errors.centreId,
    [inputs.series, inputs.qualification, inputs.assessment, inputs.component, inputs.centreId, errors.centreId],
  );

  const onPostResultSearch = () => {
    if (!isValidForm || !businessUnit) {
      return;
    }

    dispatch(getCentreWithModerators({ ...queryParams, businessUnitId: businessUnit.id }));
    setShowSearchResults(true);
  };

  const renderResults = (): JSX.Element => {
    if (!centre && !consortium) {
      return <Title data-qa-id='search-result-message'>No results found</Title>;
    }

    return (
      <>
        <Title data-qa-id='search-result-message'>1 result found</Title>
        <CentreCardContent
          centre={centre}
          consortium={consortium}
          moderators={moderators}
          allocatedModerator={allocatedModerator}
          queryParams={{ ...queryParams, businessUnitId: businessUnit?.id }}
        />
      </>
    );
  };

  if (isSeriesLoading && !inputs.series) return <Loader />;

  if (hasError) return <div>Failed to get Series data</div>;

  return (
    <>
      <Title id='series'>Select series</Title>
      <StyledInputContainer>
        <Dropdown<Series>
          data={activeSeries}
          renderOption={renderSeries}
          renderSelectedOption={renderSeries}
          dataQaId='series-select'
          onChange={onSeriesSelected}
          value={inputs.series}
          placeholder='Series'
          title='Select series'
        />
      </StyledInputContainer>
      <Title id='qualification'>Select qualification</Title>
      <StyledInputContainer>
        <Dropdown<QualificationMin>
          data={qualifications}
          renderOption={renderQualifications}
          renderSelectedOption={renderQualifications}
          dataQaId='qualification-select'
          onChange={onQualificationSelected}
          value={inputs.qualification}
          disabled={!inputs.series}
          placeholder='Qualification'
          title='Select qualification'
        />
      </StyledInputContainer>
      <Title id='syllabus'>Select {titleByBusinessId}</Title>
      <StyledInputContainer>
        <Dropdown<Assessment>
          data={assessments}
          renderOption={renderAssessments}
          renderSelectedOption={renderAssessments}
          dataQaId='syllabus-select'
          onChange={onAssessmentSelected}
          value={inputs.assessment}
          disabled={!inputs.qualification}
          placeholder={titleByBusinessId[0].toUpperCase() + titleByBusinessId.slice(1)}
          title='Select syllabus/specification'
        />
      </StyledInputContainer>
      <Title id='component'>Select component</Title>
      <StyledInputContainer>
        <Dropdown<EntryComponent>
          data={components}
          renderOption={renderComponents}
          renderSelectedOption={renderComponents}
          dataQaId='component-select'
          onChange={onComponentSelected}
          value={inputs.component}
          disabled={!inputs.assessment}
          placeholder='Component'
          title='Select component'
        />
      </StyledInputContainer>
      <StyledInputContainer>
        <Title id='centre'>Search for centre/consortium</Title>
        <TextField
          dataQaId='search-centre'
          onChange={onCentreChange}
          placeholder='Centre/consortium number'
          value={inputs.centreId}
          errorMsg={errors.centreId}
          disabled={!inputs.component}
        />
      </StyledInputContainer>
      <ButtonContainer>
        <Button disabled={!isValidForm()} onClick={onPostResultSearch} qaId='search-button'>
          Search
        </Button>
      </ButtonContainer>
      {showSearchResults && !isFetchingCentre && <>{renderResults()}</>}
      {isFetchingCentre && (
        <div>
          <ProgressContainer>
            <CircularProgressBar variant='indeterminate' size={44} />
          </ProgressContainer>
          <Paragraph>Fetching centre or consortium. Please wait</Paragraph>
        </div>
      )}
    </>
  );
};

export default Selector;
